import { ArrowDropDown, Close } from '@mui/icons-material';
import {
  ClickAwayListener,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
} from '@mui/material';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

// Función helper para obtener la key por defecto
function defaultGetOptionKey<T>(option: T, index: number): string | number {
  if (option === null || option === undefined) {
    return index;
  }

  // Si es string o number, usar el valor mismo
  if (typeof option === 'string' || typeof option === 'number') {
    return option;
  }

  // Si es un objeto, buscar id o key
  if (typeof option === 'object') {
    const obj = option as Record<string, unknown>;
    if (obj.id !== undefined && obj.id !== null) {
      return obj.id as string | number;
    }
    if (obj.key !== undefined && obj.key !== null) {
      return obj.key as string | number;
    }
  }

  return index;
}

export interface CustomAutocompleteProps<T = unknown> {
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  getOptionLabel: (option: T) => string;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  filterOptions: (options: T[], inputValue: string) => T[];
  getOptionKey?: (option: T, index: number) => string | number;
  disabled?: boolean;
  openOnFocus?: boolean;
  onEnterPress?: () => void;
}

function CustomAutocompleteInner<T = unknown>(
  {
    options,
    value,
    onChange,
    getOptionLabel,
    label,
    placeholder,
    fullWidth = false,
    filterOptions,
    getOptionKey,
    disabled = false,
    openOnFocus = true,
    onEnterPress,
  }: CustomAutocompleteProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<T[]>(options);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  // Usar función por defecto si no se proporciona getOptionKey
  const getKey = getOptionKey || defaultGetOptionKey;

  // Actualizar el valor del input cuando cambia el value prop
  useEffect(() => {
    if (value) {
      setInputValue(getOptionLabel(value));
    } else {
      setInputValue('');
    }
  }, [value, getOptionLabel]);

  // Filtrar opciones cuando cambia el inputValue
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = filterOptions(options, inputValue);
      setFilteredOptions(filtered);
      setOpen(true);
    } else {
      // Cuando está vacío, mostrar todas las opciones
      setFilteredOptions(options);
      // No cerrar automáticamente - el estado open se maneja en handleInputFocus y handleClickAway
    }
  }, [inputValue, options, filterOptions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  const handleOptionClick = (option: T) => {
    setInputValue(getOptionLabel(option));
    onChange(option);
    setOpen(false);
  };

  const handleClickAway = () => {
    setOpen(false);
    // Si hay un valor seleccionado, restaurar el label
    if (value) {
      setInputValue(getOptionLabel(value));
    } else {
      setInputValue('');
    }
  };

  const handleInputFocus = () => {
    if (openOnFocus && options.length > 0) {
      setOpen(true);
      // Si el input está vacío, mostrar todas las opciones
      if (!inputValue.trim()) {
        setFilteredOptions(options);
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0 && !value) {
        // Si hay opciones filtradas y no hay valor seleccionado: seleccionar primera opción
        handleOptionClick(filteredOptions[0]);
        // Llamar onEnterPress después de un pequeño delay para permitir que el estado se actualice
        if (onEnterPress) {
          setTimeout(() => {
            onEnterPress();
          }, 0);
        }
      } else if (value && onEnterPress) {
        // Si ya hay valor seleccionado: solo avanzar
        onEnterPress();
      } else if (filteredOptions.length > 0) {
        // Fallback: si hay opciones pero no hay callback, solo seleccionar
        handleOptionClick(filteredOptions[0]);
      }
    }
  };

  const handleClear = () => {
    setInputValue('');
    onChange(null);
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div ref={anchorRef} style={{ width: fullWidth ? '100%' : 'auto' }}>
        <TextField
          fullWidth={fullWidth}
          label={label}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          inputRef={inputRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {value ? (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    edge="end"
                    aria-label="clear"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                ) : options.length > 0 ? (
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label="show options"
                    onClick={handleInputFocus}
                  >
                    <ArrowDropDown fontSize="small" />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
          }}
        />
        {open && filteredOptions.length > 0 && (
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
          >
            <Paper
              style={{
                maxHeight: 300,
                overflow: 'auto',
                width: '100%',
              }}
            >
              <List>
                {filteredOptions.map((option, index) => (
                  <ListItem key={getKey(option, index)} disablePadding>
                    <ListItemButton onClick={() => handleOptionClick(option)}>
                      <ListItemText primary={getOptionLabel(option)} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Popper>
        )}
      </div>
    </ClickAwayListener>
  );
}

const CustomAutocomplete = forwardRef(CustomAutocompleteInner) as <T = unknown>(
  props: CustomAutocompleteProps<T> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
  },
) => React.ReactElement;

export default CustomAutocomplete;
